// Source-map resolver.
//
// Turns a persisted BookStructure + the raw extracted source text into
// the shape the two-pane review UI needs: every section resolved to
// character offsets, plus derived gaps (source text in no section).
//
// Called at read-time by the /source-documents/{id}/map endpoint. Nothing
// here writes to Mongo — offsets and gaps are ephemeral, recomputed on
// each request so they always agree with the current source text +
// current header_pattern strings. That trades a small amount of compute
// per request (regex passes over one chapter's text) for eliminating a
// whole class of "stored offsets don't match the current source text"
// staleness bugs.
//
// The header-pattern matching itself reuses the ingestion-time helpers
// at packages/api/src/shared/ingestion/header-match.ts — same normalise-
// then-match logic the chunker + chunk-size-estimator use, so the
// review UI paints spans at exactly the char ranges the chunker will
// slice at ingestion time. Behaviour parity is the whole point: if the
// review UI shows a boundary at char 4200, the chunker MUST cut there
// too, or the reviewer is approving something different from what gets
// ingested.

// Same-package imports (this file lives in @gc-coach/shared) — types
// come from source-document-mapping-api and the header-match helpers
// from the sibling module. Both were previously in packages/api; moved
// here so the frontend can re-resolve client-side after edits (source-
// map redesign MR 8).
import type {
  BookChapterDef,
  BookStructure,
} from './source-document-mapping-api.js';
import type {
  MapGap,
  ResolvedBookStructure,
  ResolvedChapter,
  ResolvedSubSection,
} from './source-document-mapping-api.js';
import { findLastMatch, normalizeForMatch } from './header-match.js';

/**
 * Minimum word count for a gap to be surfaced in the UI. Below this we
 * assume it's whitespace / a chapter heading / a stray line break rather
 * than genuinely orphaned content. Tune if reviewers report missing gaps
 * or getting spammed by trivial ones.
 */
const MIN_GAP_WORD_COUNT = 20;

/**
 * Resolve every chapter + sub-section to (char_start, char_end) offsets
 * in `sourceText`. Uses `findLastMatch` for both levels — same TOC-skip
 * heuristic the chunker uses (a book's chapter title typically appears
 * twice: TOC entry near the top, real heading later; sub-section titles
 * often appear in a chapter-intro enumeration + at the real heading;
 * "last occurrence" wins in both cases).
 *
 * Missing matches (Sonnet proposed a header that doesn't actually appear
 * in the extracted text) return `null` offsets — the caller renders the
 * tree row as a boundary issue and the doc pane draws a red inline
 * strip at the expected position.
 */
export function resolveChapters(
  sourceText: string,
  chapters: readonly BookChapterDef[],
): ResolvedChapter[] {
  const normalized = normalizeForMatch(sourceText);

  // Chapter offsets first (pass 1) — we need all of them to compute each
  // chapter's char_end (= next chapter's char_start).
  const chapterOffsets: Array<number | null> = chapters.map((c) =>
    findLastMatch(normalized, c.header_pattern),
  );

  return chapters.map((chapter, i) => {
    const char_start = chapterOffsets[i]!;

    // char_end = start of the next chapter that HAS a match; falls
    // through to EOF if every subsequent chapter also failed to match.
    let char_end: number | null = null;
    for (let j = i + 1; j < chapters.length; j++) {
      const next = chapterOffsets[j];
      if (typeof next === 'number') {
        char_end = next;
        break;
      }
    }
    if (char_end === null) char_end = normalized.length;

    // Sub-section pass — scoped to this chapter's char range. We slice
    // the normalized text to that range, run findLastMatch inside it,
    // then translate offsets back to document space by adding char_start.
    let resolvedSubs: ResolvedSubSection[];
    if (char_start !== null && chapter.sub_sections.length > 0) {
      const chapterText = normalized.slice(char_start, char_end);
      const subOffsets: Array<number | null> = chapter.sub_sections.map((s) =>
        findLastMatch(chapterText, s.header_pattern),
      );
      resolvedSubs = chapter.sub_sections.map((sub, k) => {
        const relStart = subOffsets[k];
        const subStart = typeof relStart === 'number' ? char_start + relStart : null;

        // sub's char_end = char_start of the next resolved sub in this
        // chapter, or the chapter's char_end.
        let subEnd: number | null = null;
        for (let m = k + 1; m < chapter.sub_sections.length; m++) {
          const nextRel = subOffsets[m];
          if (typeof nextRel === 'number') {
            subEnd = char_start + nextRel;
            break;
          }
        }
        if (subEnd === null && subStart !== null) subEnd = char_end;

        return {
          ...sub,
          char_start: subStart,
          char_end: subEnd,
        };
      });
    } else {
      // Chapter didn't match OR has no sub-sections: pass sub-sections
      // through with null offsets so the tree row still renders (with
      // a boundary-issue pill for a chapter miss).
      resolvedSubs = chapter.sub_sections.map((sub) => ({
        ...sub,
        char_start: null,
        char_end: null,
      }));
    }

    return {
      ...chapter,
      char_start,
      char_end,
      sub_sections: resolvedSubs,
    };
  });
}

/**
 * Derive gaps — source text ranges NOT covered by any resolved section.
 *
 * "Covered" means:
 *   - A chapter with sub-sections is covered where its RESOLVED sub-
 *     sections are (each sub's [char_start, char_end) range).
 *   - A chapter WITHOUT sub-sections is covered across its whole span.
 *   - A chapter that failed to resolve at all contributes nothing to
 *     coverage — its content becomes gaps by definition.
 *   - Sub-sections marked `excluded` STILL count as covered ranges here;
 *     they exist in the map (the editor explicitly assigned that text to
 *     a section), they just won't be ingested. Rendering them as gaps
 *     would suggest the content is unassigned, which is wrong.
 *
 * Gaps below `minWordCount` are dropped — those are almost always
 * whitespace between headings or trivial fragments that would just
 * clutter the review UI.
 *
 * Returned gaps are sorted by char_start and are non-overlapping.
 */
export function deriveGaps(
  sourceText: string,
  resolvedChapters: readonly ResolvedChapter[],
  minWordCount: number = MIN_GAP_WORD_COUNT,
): MapGap[] {
  const normalized = normalizeForMatch(sourceText);
  const textLength = normalized.length;

  // Collect [start, end) ranges from every resolved section.
  const coveredRanges: Array<[number, number]> = [];
  for (const chapter of resolvedChapters) {
    if (chapter.char_start === null || chapter.char_end === null) continue;
    if (chapter.sub_sections.length === 0) {
      coveredRanges.push([chapter.char_start, chapter.char_end]);
      continue;
    }
    for (const sub of chapter.sub_sections) {
      if (sub.char_start === null || sub.char_end === null) continue;
      coveredRanges.push([sub.char_start, sub.char_end]);
    }
  }

  // Sort by start, then merge any overlaps (defensive — shouldn't happen
  // with well-formed maps, but if the resolver ever produces overlapping
  // ranges we don't want that to break the gap complement).
  coveredRanges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const [start, end] of coveredRanges) {
    const last = merged[merged.length - 1];
    if (last && last[1] >= start) {
      last[1] = Math.max(last[1], end);
    } else {
      merged.push([start, end]);
    }
  }

  // Gap complement over [0, textLength).
  const gaps: MapGap[] = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    if (start > cursor) {
      pushGap(gaps, normalized, cursor, start, minWordCount);
    }
    cursor = end;
  }
  if (cursor < textLength) {
    pushGap(gaps, normalized, cursor, textLength, minWordCount);
  }

  return gaps;
}

/**
 * The read-time entry point: takes the persisted BookStructure + the raw
 * source text and returns the shape the two-pane review UI consumes.
 *
 * source_text on the returned shape is the NORMALISED text (curly quotes
 * → straight, NBSP → space, zero-width chars stripped) so char offsets
 * agree with what the UI actually renders. Frontend must render this
 * string verbatim, not re-normalise on its own.
 */
export function resolveBookStructure(
  structure: BookStructure,
  sourceText: string,
): ResolvedBookStructure {
  const chapters = resolveChapters(sourceText, structure.chapters);
  const gaps = deriveGaps(sourceText, chapters);
  const normalizedSource = normalizeForMatch(sourceText);
  // Destructure to keep TypeScript happy about the `chapters` swap — the
  // Omit<> in ResolvedBookStructure removes the base `chapters` field.
  const { chapters: _base, ...rest } = structure;
  void _base;
  return {
    ...rest,
    chapters,
    gaps,
    source_text: normalizedSource,
  };
}

function pushGap(
  gaps: MapGap[],
  normalizedText: string,
  start: number,
  end: number,
  minWordCount: number,
): void {
  const wc = countWords(normalizedText.slice(start, end));
  if (wc >= minWordCount) {
    gaps.push({ char_start: start, char_end: end, word_count: wc });
  }
}

function countWords(text: string): number {
  const trimmed = text.trim();
  if (trimmed.length === 0) return 0;
  return trimmed.split(/\s+/).length;
}
